import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Pagination,
  IconButton,
  MenuItem,
  Menu,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Button,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getFlaggedPosts, approveFlaggedPost, blockFlaggedPost } from "store/actions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FlagIcon from "@mui/icons-material/Flag";

const FlaggedPostsManager = () => {
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const [postsData, setPostsData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [menuAnchorEls, setMenuAnchorEls] = useState({});
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  useEffect(() => {
    getAllFlaggedPostsData();
  }, [dispatch, page]);

  const getAllFlaggedPostsData = () => {
    dispatch(
      getFlaggedPosts(page + 1, 10, searchTerm, (result) => {
        setPostsData(result.posts);
        setTotalPages(result.totalPages);
      })
    );
  };

  const handleChangePage = (event, value) => {
    setPage(value - 1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  useEffect(() => {
    let timerOut = setTimeout(() => {
      getAllFlaggedPostsData();
    }, 800);
    return () => clearTimeout(timerOut);
  }, [searchTerm]);

  const handleMenuClick = (event, id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  const handleView = (post) => {
    setSelectedPost(post);
    setOpenDetailDialog(true);
    handleMenuClose(post._id);
  };

  const handleApprove = (postId) => {
    setActionInProgress(true);
    dispatch(
      approveFlaggedPost(postId, (result) => {
        setActionInProgress(false);
        getAllFlaggedPostsData();
        setOpenDetailDialog(false);
      })
    );
    handleMenuClose(postId);
  };

  const handleBlock = (postId) => {
    setActionInProgress(true);
    dispatch(
      blockFlaggedPost(postId, (result) => {
        setActionInProgress(false);
        getAllFlaggedPostsData();
        setOpenDetailDialog(false);
      })
    );
    handleMenuClose(postId);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Left: Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <FlagIcon sx={{ color: "error.main" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Review Flagged Posts
              </Typography>
            </Box>

            {/* Right: Search Field */}
            <TextField
              label="Search Posts"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by title, content, or author name"
              sx={{
                minWidth: { xs: "100%", sm: 260, md: 300 },
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />
          </Paper>


          <Grid container spacing={3} sx={{ marginTop: "8px" }}>
            {postsData.length > 0 ? (
              postsData.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post._id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      border: "2px solid",
                      borderColor: "error.main",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(244,67,54,0.3)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        borderBottom: "1px solid #eee",
                        backgroundColor: "rgba(244, 67, 54, 0.05)",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={post?.author?.profileImage?.signedUrl}
                          alt={post?.author?.name}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {post?.author?.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(post?.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Chip
                          icon={<FlagIcon />}
                          label={`${post?.reports?.length || 0} Reports`}
                          color="error"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, post._id)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                      <Menu
                        anchorEl={menuAnchorEls[post._id]}
                        open={Boolean(menuAnchorEls[post._id])}
                        onClose={() => handleMenuClose(post._id)}
                      >
                        <MenuItem onClick={() => handleView(post)}>
                          <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
                          View Details
                        </MenuItem>
                      </Menu>
                    </Box>

                    {post?.attachment?.signedUrl && (
                      <Box
                        sx={{
                          width: "100%",
                          backgroundColor: "#000",
                          aspectRatio: "1",
                          overflow: "hidden",
                          display: "flex",
                          height: "150px",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {post.attachment.mimeType?.startsWith("video") ? (
                          <video
                            src={post.attachment.signedUrl}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                            controls
                          />
                        ) : (
                          <img
                            src={post.attachment.signedUrl}
                            alt={post.title}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                          />
                        )}
                      </Box>
                    )}

                    <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                      {post?.title && (
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          {post.title}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          mb: 2,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 3,
                        }}
                      >
                        {post?.content}
                      </Typography>

                      <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                        {post?.topicTag && (
                          <Chip label={post.topicTag} size="small" variant="outlined" />
                        )}
                        {post?.phaseTag && (
                          <Chip label={post.phaseTag} size="small" variant="outlined" />
                        )}
                      </Box>
                    </CardContent>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        p: 2,
                        borderTop: "1px solid #eee",
                      }}
                    >
                      {post?.status === 'approved' && (
                        <>
                          <Button fullWidth variant="outlined" color="success" size="small" disabled startIcon={<CheckCircleIcon />}>
                            Approved
                          </Button>
                          <Button
                            fullWidth
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<BlockIcon />}
                            onClick={() => handleBlock(post._id)}
                            disabled={actionInProgress}
                          >
                            Block
                          </Button>
                        </>
                      )}
                      {post?.status === 'blocked' && (
                        <>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApprove(post._id)}
                            disabled={actionInProgress}
                          >
                            Approve
                          </Button>
                          <Button fullWidth variant="outlined" color="error" size="small" disabled startIcon={<BlockIcon />}>
                            Blocked
                          </Button>
                        </>
                      )}
                      {(!post?.status || post?.status === 'pending') && (
                        <>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApprove(post._id)}
                            disabled={actionInProgress}
                          >
                            Approve
                          </Button>
                          <Button
                            fullWidth
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<BlockIcon />}
                            onClick={() => handleBlock(post._id)}
                            disabled={actionInProgress}
                          >
                            Block
                          </Button>
                        </>
                      )}
                    </Box>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <FlagIcon sx={{ fontSize: 48, color: "textSecondary", mb: 2 }} />
                  <Typography variant="body2" color="textSecondary">
                    No flagged posts found
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid container justifyContent="center" mt={3}>
        <Pagination
          count={Math.ceil(totalPages)}
          color="secondary"
          size="small"
          page={page + 1}
          onChange={handleChangePage}
        />
      </Grid>

      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Flagged Post Details</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedPost && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Author:</strong> {selectedPost?.author?.name}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Email:</strong> {selectedPost?.author?.email}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Title:</strong> {selectedPost?.title || "Untitled"}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Content:</strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                {selectedPost?.content}
              </Typography>

              {selectedPost?.attachment?.signedUrl && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Media:</strong>
                  </Typography>
                  {selectedPost.attachment.mimeType?.startsWith("video") ? (
                    <video
                      src={selectedPost.attachment.signedUrl}
                      style={{
                        width: "100%",
                        maxHeight: "400px",
                        borderRadius: "8px",
                        backgroundColor: "#000",
                      }}
                      controls
                    />
                  ) : (
                    <img
                      src={selectedPost.attachment.signedUrl}
                      alt={selectedPost.title}
                      style={{
                        width: "100%",
                        maxHeight: "400px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>
              )}

              <Typography variant="subtitle2" gutterBottom>
                <strong>Posted:</strong> {formatDate(selectedPost?.createdAt)}
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: "error.main" }}>
                  <FlagIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Reports ({selectedPost?.reports?.length || 0})
                </Typography>
                {selectedPost?.reports && selectedPost.reports.length > 0 ? (
                  selectedPost.reports.map((report, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Reported by:</strong> {report?.user?.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Email:</strong> {report?.user?.email}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Reason:</strong> {report?.reason}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(report?.createdAt)}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No reports
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenDetailDialog(false)}>Close</Button>
          {selectedPost?.status === 'approved' && (
            <>
              <Button variant="outlined" color="success" startIcon={<CheckCircleIcon />} disabled>
                Approved
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => handleBlock(selectedPost?._id)}
                disabled={actionInProgress}
              >
                Block Post
              </Button>
            </>
          )}
          {selectedPost?.status === 'blocked' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleApprove(selectedPost?._id)}
                disabled={actionInProgress}
              >
                Approve
              </Button>
              <Button variant="outlined" color="error" startIcon={<BlockIcon />} disabled>
                Blocked
              </Button>
            </>
          )}
          {(!selectedPost?.status || selectedPost?.status === 'pending') && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleApprove(selectedPost?._id)}
                disabled={actionInProgress}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => handleBlock(selectedPost?._id)}
                disabled={actionInProgress}
              >
                Block Post
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FlaggedPostsManager;
